import { FieldProps } from '@rjsf/utils';
import { Multiselect, MultiselectProps } from '@amzn/awsui-components-react-v3';
import { useEffect, useMemo } from 'react';
import React from 'react';

export interface TreeNode {
  id: string;
  label: string;
  type?: string;
  selected: boolean;
  disabled?: boolean;
  children?: TreeNode[];
}

/**
 * Flattens selected nodes into human-readable lines like:
 * "AccountId → Admin → aws-dgs-sde"
 */
export function flattenTreeToText(nodes: TreeNode[], path: string[] = []): string[] {
  const lines: string[] = [];
  for (const node of nodes) {
    if (!node.selected && (!node.children || node.children.length === 0)) continue;
    const currentPath = [...path, node.label];
    if (node.selected) lines.push(currentPath.join(' → '));
    if (node.children) {
      lines.push(...flattenTreeToText(node.children, currentPath));
    }
  }
  return lines;
}

/** Separator for path-based keys to avoid collisions with ids containing '/' */
const PATH_SEP = '\0';

/** Builds a path-based key that is unique across the entire tree */
function pathKey(parentPath: string, id: string): string {
  return parentPath ? `${parentPath}${PATH_SEP}${id}` : id;
}

/** Walks the tree collecting path keys, optionally filtered by a predicate */
function collectPathKeys(nodes: TreeNode[], parentPath = '', predicate?: (node: TreeNode) => boolean): string[] {
  const keys: string[] = [];
  for (const node of nodes) {
    const key = pathKey(parentPath, node.id);
    if (!predicate || predicate(node)) keys.push(key);
    if (node.children) keys.push(...collectPathKeys(node.children, key, predicate));
  }
  return keys;
}

/**
 * Recursively collects all descendant nodes as flat options.
 * Uses path-based keys as values to ensure uniqueness across the tree.
 */
function collectOptions(nodes: TreeNode[], parentPath: string, parentLabel: string): MultiselectProps.Option[] {
  const opts: MultiselectProps.Option[] = [];
  for (const node of nodes) {
    const key = pathKey(parentPath, node.id);
    const label = parentLabel ? `${parentLabel} → ${node.label}` : node.label;
    opts.push({ value: key, label, disabled: node.disabled });
    if (node.children && node.children.length > 0) {
      opts.push(...collectOptions(node.children, key, label));
    }
  }
  return opts;
}

/** Converts tree into Cloudscape grouped options. Top-level nodes = groups. */
function buildGroupedOptions(nodes: TreeNode[]): (MultiselectProps.Option | MultiselectProps.OptionGroup)[] {
  return nodes.map((node) => {
    if (node.children && node.children.length > 0) {
      return { label: node.label, options: collectOptions(node.children, node.id, '') };
    }
    return { value: node.id, label: node.label, disabled: node.disabled };
  });
}

/** Builds a map of path key → all descendant path keys for cascade toggling */
function buildDescendantMap(nodes: TreeNode[], parentPath = ''): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const node of nodes) {
    const key = pathKey(parentPath, node.id);
    const descendants = node.children ? collectPathKeys(node.children, key) : [];
    if (descendants.length > 0) map.set(key, descendants);
    if (node.children) {
      for (const [k, v] of buildDescendantMap(node.children, key)) {
        map.set(k, v);
      }
    }
  }
  return map;
}

/** Sets selected state on all nodes based on a set of path keys */
function applySelections(nodes: TreeNode[], selectedKeys: Set<string>, parentPath = ''): TreeNode[] {
  return nodes.map((node) => {
    const key = pathKey(parentPath, node.id);
    return {
      ...node,
      selected: selectedKeys.has(key),
      children: node.children ? applySelections(node.children, selectedKeys, key) : node.children,
    };
  });
}

/**
 * Builds a flat pathKey→label map matching the dropdown option labels.
 * Top-level nodes use their own label; descendants use "Parent → Child" format.
 */
function buildTokenLabelMap(nodes: TreeNode[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const node of nodes) {
    map.set(node.id, node.label);
    if (node.children) {
      for (const opt of collectOptions(node.children, node.id, '')) {
        if (opt.value) map.set(opt.value, opt.label!);
      }
    }
  }
  return map;
}

/**
 * Custom field that renders a hierarchical tree as a grouped Multiselect.
 * Top-level nodes become group headers, all descendants become selectable options.
 * Selecting a parent cascades to all children, even disabled ones.
 *
 * Uses path-based keys (parent\0child\0grandchild) instead of bare ids to handle
 * cases where the same id appears under different parents.
 */
export function HierarchicalCheckboxField(props: FieldProps) {
  const nodes: TreeNode[] = Array.isArray(props.formData) ? props.formData : [];
  const nodesKey = JSON.stringify(nodes);

  const groups = useMemo(() => buildGroupedOptions(nodes), [nodesKey]);
  const labelMap = useMemo(() => buildTokenLabelMap(nodes), [nodesKey]);
  const descendantMap = useMemo(() => buildDescendantMap(nodes), [nodesKey]);
  const prevSelectedKeys = useMemo(() => new Set(collectPathKeys(nodes, '', (n) => n.selected)), [nodesKey]);

  const selectedOptions = useMemo(
    () => Array.from(prevSelectedKeys).map((key) => ({ value: key, label: labelMap.get(key) || key })),
    [prevSelectedKeys, labelMap],
  );

  const handleChange: MultiselectProps['onChange'] = ({ detail }) => {
    if (props.readonly || props.disabled) return;
    const newSelectedKeys = new Set(detail.selectedOptions.map((opt) => opt.value!));

    // Cascade: if a parent was just toggled, apply same state to all its descendants
    for (const [parentKey, descendantKeys] of descendantMap) {
      const wasSelected = prevSelectedKeys.has(parentKey);
      const isSelected = newSelectedKeys.has(parentKey);
      if (wasSelected !== isSelected) {
        for (const childKey of descendantKeys) {
          if (isSelected) {
            newSelectedKeys.add(childKey);
          } else {
            newSelectedKeys.delete(childKey);
          }
        }
      }
    }

    const updated = applySelections(nodes, newSelectedKeys);
    props.formContext?.onTreeFieldChange?.(props.name, updated);
    props.onChange(updated);
  };

  // Seed the ref on initial render so data is preserved even before any change
  useEffect(() => {
    if (nodes.length > 0) {
      props.formContext?.onTreeFieldChange?.(props.name, nodes);
    }
  }, [nodesKey, props.formContext, props.name]);

  if (nodes.length === 0) return null;

  return (
    <Multiselect
      selectedOptions={selectedOptions}
      onChange={handleChange}
      options={groups}
      placeholder='Choose applicable options'
      filteringType='auto'
      disabled={props.disabled || props.readonly}
    />
  );
}
